import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { ChakraUIProvider } from 'reactifi';
import { ProgressSpinner } from '@everfi/reactifi/src/components/progress-spinner';
import ModuleCompleteNextSteps from '../../../components/ModuleCompleteNextSteps';
import {
  completedContentId,
  dashboardUrlWithoutPanel,
  loadMoreToExplore,
  nextStepsTitle,
  upNextFromPlaylist,
} from '../services/nextSteps';

const NextStepsPanel = ({ currentUriContext, isGuest, onLogin, playlist, programId, programSlug }) => {
  const contentId = completedContentId();
  const [moreToExplore, setMoreToExplore] = useState(null);

  useEffect(() => {
    loadMoreToExplore({ programId, programSlug, contentId })
      .then(setMoreToExplore)
      .catch(() => setMoreToExplore([]));
  }, [contentId, programId, programSlug]);

  const { completedModule, upNext } = upNextFromPlaylist(playlist, contentId, currentUriContext);

  const returnToPathway = () => window.location.assign(dashboardUrlWithoutPanel());

  // playlist arrives after program, so Up Next is unknowable until both it and recommendations answer
  const settled = !!playlist && moreToExplore !== null;

  // the dashboard keeps loading after this mounts, so playlist can churn and upNext can briefly
  // read empty. Dismissal has to stay one-way: once the panel has shown content, a later empty
  // read must not unmount it out from under whoever is using it.
  const hasShownContent = useRef(false);
  if (settled && (upNext || moreToExplore.length)) hasShownContent.current = true;

  const nothingToShow = settled && !upNext && !moreToExplore.length && !hasShownContent.current;

  // replace, not assign: this redirect is automatic, so it must not leave the panel URL in history
  useEffect(() => {
    if (nothingToShow) window.location.replace(dashboardUrlWithoutPanel());
  }, [nothingToShow]);

  if (nothingToShow) return null;
  if (!settled) return <ProgressSpinner />;

  return (
    <ChakraUIProvider>
      <ModuleCompleteNextSteps
        completedModuleName={nextStepsTitle(completedModule, playlist)}
        upNext={upNext}
        moreToExplore={moreToExplore}
        onReturn={returnToPathway}
        isGuest={isGuest}
        onLogin={onLogin}
      />
    </ChakraUIProvider>
  );
};

NextStepsPanel.propTypes = {
  currentUriContext: PropTypes.string,
  isGuest: PropTypes.bool,
  onLogin: PropTypes.func,
  playlist: PropTypes.object,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  programSlug: PropTypes.string,
};

export default NextStepsPanel;
